/**
 * global-api.ts — Cross-MFE bridge for dynamic theming via CustomEvents.
 *
 * The color picker lives in embed-dev-portal (a separate MFE) but the
 * theme engine lives in blink-v2. We use DOM CustomEvents to bridge the
 * gap — this is the standard pattern for cross-MFE communication.
 *
 * Flow:
 *   1. blink-v2 calls registerDynamicThemeListener() on mount
 *   2. embed-dev-portal dispatches 'radiant-theme:apply' / 'radiant-theme:clear'
 *   3. blink-v2's listener receives the event and calls applyTheme/clearTheme
 */

import { addCustomCssElements } from '/@utils/embed.util';
import { applyTheme, clearTheme } from './apply-theme';

// =========================================================================
// Event type constants — shared between producer and consumer
// =========================================================================

/** Dispatched when a custom theme should be applied from a source color. */
export const THEME_APPLY_EVENT = 'radiant-theme:apply';

/** Dispatched when the custom theme should be cleared (revert to defaults). */
export const THEME_CLEAR_EVENT = 'radiant-theme:clear';

/** Dispatched when the nav theme bridge should activate/deactivate. */
export const THEME_NAV_EVENT = 'radiant-theme:nav';

// =========================================================================
// Listener (blink-v2 side)
// =========================================================================

export interface DynamicThemeListenerOptions {
    /** Called when a 'radiant-theme:nav' event fires (toggle on/off). */
    onNavModeChange?: (detail: { active: boolean; mode?: string }) => void;
}

/**
 * Register event listeners for dynamic theme events.
 * Call this once during app initialization (e.g., in authenticated-app-layout).
 *
 * Listens for:
 *   - 'radiant-theme:apply' with detail.sourceHex
 *   - 'radiant-theme:clear' to revert to defaults
 *   - 'radiant-theme:nav'   with detail.active + detail.mode
 */
export function registerDynamicThemeListener(
    options?: DynamicThemeListenerOptions,
): () => void {
    const handleApply = (event: Event) => {
        const { sourceHex, variant } = (event as CustomEvent).detail ?? {};
        if (sourceHex) {
            const themeVars = applyTheme(sourceHex, { variant });

            // Bridge into the embed CSS infrastructure so that
            // getEmbedCssParams / getEmbedCssStyles see the tokens.
            addCustomCssElements({
                customizations: {
                    style: {
                        customCSS: { variables: themeVars },
                    },
                },
            });
        }
    };

    const handleClear = () => {
        clearTheme();

        // Also clear the <style> element injected by addCustomCssElements
        // during brand theme apply. Passing empty variables removes the
        // old element and replaces it with an empty one.
        addCustomCssElements({
            customizations: {
                style: {
                    customCSS: { variables: {} },
                },
            },
        });
    };

    const handleNav = (event: Event) => {
        const detail = (event as CustomEvent).detail ?? {};
        options?.onNavModeChange?.(detail);
    };

    document.addEventListener(THEME_APPLY_EVENT, handleApply);
    document.addEventListener(THEME_CLEAR_EVENT, handleClear);
    document.addEventListener(THEME_NAV_EVENT, handleNav);

    return () => {
        document.removeEventListener(THEME_APPLY_EVENT, handleApply);
        document.removeEventListener(THEME_CLEAR_EVENT, handleClear);
        document.removeEventListener(THEME_NAV_EVENT, handleNav);
    };
}
